import { FC, useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "@remix-run/react";
import { camelizeKeys } from "humps";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./contact-button-configured.module.css";
import {
  goToWhatApp,
  getDomain,
  makeUrl,
  AddonTypeId,
  getAddonFromStoreAddons,
  useAnalytics } from
"@commons-monorepo/index";
import { ContactButtonIconName, ContactButtonMessageWordsToReplace } from "../../constants/contact-button.constants";
import { ContactButtonEditPayload } from "../../entities/contact-button.entities";
import { EVENTS } from "~/constants/events";

interface ContactButtonConfiguredProps {
  isInPreview?: boolean;
  buttonConfig?: ContactButtonEditPayload;
  productName?: string;
}

const ContactButtonConfigured: FC<ContactButtonConfiguredProps> = ({ isInPreview, productName, buttonConfig }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  let [searchParams] = useSearchParams();
  const hiddenNetworkLinks = searchParams.get("hideNetworkButtons");
  const [data, setData] = useState<ContactButtonEditPayload | undefined>(buttonConfig);
  const { storeMetadata, userAddons } = useOutletContext<any>();
  const [logEvent] = useAnalytics({ storeMetadata });
  const showButton = !!data && (isInPreview || !pathname.includes("/contact-addon"));

  const shouldInstagramButtonHide = isInPreview ?
  false :
  data?.wp_contact?.configuration?.is_instagram_hidden || !data?.wp_contact?.configuration?.instagram_username;

  const hideContactButtonInstagram =
  data?.wp_contact?.configuration?.is_instagram_hidden === true ||
  data?.wp_contact?.configuration?.is_instagram_hidden === undefined;

  const hideContactButtonWhatsapp =
  data?.wp_contact?.configuration?.is_hidden === true || data?.wp_contact?.configuration?.is_hidden === undefined;

  const logEventClick = (source: string) => {
    logEvent(EVENTS.SELECT_BUTTON, {
      event_subtype: "web_floating_button_contact",
      option: source,
      user_type: "buyer",
      platform: "web"
    });
  };

  const handleWhatsappContactClick = () => {
    const isInProductPage = pathname.includes("/producto");
    const defaultMessage = t("footer.text_whats_app_store", {
      link: encodeURIComponent(`${getDomain()}${makeUrl(pathname, ``)}`)
    });

    const message =
    (isInProductPage ?
    data?.wp_contact?.messages?.product?.
    replaceAll(ContactButtonMessageWordsToReplace.ProductName, productName || "").
    replaceAll(
      ContactButtonMessageWordsToReplace.ProductLink,
      encodeURIComponent(`${location.origin}${pathname}`)
    ) :
    data?.wp_contact?.messages?.general?.
    replaceAll(ContactButtonMessageWordsToReplace.StoreName, storeMetadata?.enterpriseName || "").
    replaceAll(
      ContactButtonMessageWordsToReplace.StoreLink,
      encodeURIComponent(`${location.origin}${pathname}`)
    )) || defaultMessage;
    const decoded = decodeURIComponent(message);
    const encode = encodeURI(decoded);
    goToWhatApp(
      `${data?.wp_contact?.configuration?.phone?.prefix}${data?.wp_contact?.configuration?.phone?.number}`,
      encode
    );
    logEventClick("whatsapp");
  };

  const handleInstagramContactClick = () => {
    if (!!data?.wp_contact?.configuration?.instagram_username) {
      logEventClick("instagram");
      window.open(`https://ig.me/m/${data?.wp_contact?.configuration?.instagram_username}`, "_blank");
    }
  };

  useEffect(() => {
    if (!isInPreview) {
      const contactButtonAddon = getAddonFromStoreAddons<ContactButtonEditPayload>(
        userAddons,
        AddonTypeId.FloatingButton
      );
      if (!contactButtonAddon?.body?.wp_contact && !!storeMetadata.catalogueWpContact) {
        const oldWpContact: ContactButtonEditPayload = {
          wp_contact: camelizeKeys(storeMetadata.catalogueWpContact)
        };
        setData(oldWpContact);
      } else if (!!contactButtonAddon?.body?.wp_contact) {
        setData(contactButtonAddon?.body);
      }
    }
  }, []);

  useEffect(() => {
    if (!!buttonConfig) setData(buttonConfig);
  }, [buttonConfig]);

  return showButton ?
  <div
    className={`
        ${styles.wrapper}
        ${styles[data?.wp_contact?.position?.horizontal || ""]}
        ${styles[data?.wp_contact?.position?.vertical || ""]}
      `}>

      <div
      className={`
          ${styles.contactButton} 
          ${styles.instagram}
          ${hideContactButtonInstagram || hiddenNetworkLinks ? styles.contactButton : styles.show}
        `}
      style={{
        backgroundColor: data?.wp_contact?.colors?.instagram_background,
        color: data?.wp_contact?.colors?.icon
      }}
      onClick={handleInstagramContactClick}>

        <span className={`icon-${data?.wp_contact?.instagram_icon || ContactButtonIconName.InstagramOne}`} />
      </div>
      <div
      className={`
          ${styles.contactButton} 
          ${styles.whatsapp}
          ${hideContactButtonWhatsapp || hiddenNetworkLinks ? styles.contactButton : styles.show}
        `}
      style={{
        backgroundColor: data?.wp_contact?.colors?.background,
        color: data?.wp_contact?.colors?.icon
      }}
      onClick={handleWhatsappContactClick}>

        <span className={`icon-${data?.wp_contact?.icon || ContactButtonIconName.One}`} />
      </div>
    </div> :
  null;
};

export default ContactButtonConfigured;